// Đối soát cùng một chỉ số giữa các bộ phận: mỗi API đang đếm theo cách riêng nên số dễ lệch nhau.
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { DEPT, fmtMoney, type Snapshot, type Totals } from './insights';
import { Panel } from './ui';

type Src = 'page' | 'tele' | 'sale' | 'appt';
interface Row {
  key: string; label: string; money?: boolean;
  values: Partial<Record<Src, number>>;
  /** Mỗi nguồn đang đếm theo cách nào */
  how: Partial<Record<Src, string>>;
  /** Một câu: vì sao lệch */
  cause: string;
}

const SRC: Record<Src, { label: string; color: string }> = {
  page: { label: DEPT.page.label, color: DEPT.page.color },
  tele: { label: DEPT.tele.label, color: DEPT.tele.color },
  sale: { label: DEPT.sale.label, color: DEPT.sale.color },
  appt: { label: 'Lịch hẹn', color: '#64748b' },
};
const ORDER: Src[] = ['page', 'tele', 'sale', 'appt'];

/** Lệch khi chênh hơn 10% số lớn nhất (tối thiểu 1) */
export const isMismatch = (vals: number[]) => {
  if (vals.length < 2) return false;
  const max = Math.max(...vals);
  return max - Math.min(...vals) > Math.max(1, max * 0.1);
};

export function reconcileRows(cur: Snapshot, c: Totals): Row[] {
  const appt = (cur.appt || []).filter(s => s.status !== 'all');
  const apptOf = (st: string) => appt.find(s => String(s.status) === st)?.count;
  const apptTotal = appt.length ? appt.reduce((t, s) => t + s.count, 0) : undefined;
  const rows: Row[] = [
    {
      key: 'appts', label: 'Lịch hẹn', values: { page: c.pageAppts, tele: c.booked, appt: apptTotal },
      how: { page: 'theo ngày tạo', tele: 'lịch có Telesale', appt: 'theo ngày hẹn' },
      cause: 'Mỗi nơi lấy mốc ngày khác nhau.',
    },
    {
      key: 'showup', label: 'Khách đến cơ sở', values: { tele: c.teleShowup, sale: c.showup, appt: apptOf('4') },
      how: { tele: 'lịch có Telesale', sale: 'lịch đã gán Sale', appt: 'lịch "Đã thực hiện"' },
      cause: 'Khách đến chưa được gán Sale cơ sở / Telesale thì bị bỏ sót.',
    },
    {
      key: 'closed', label: 'Khách chốt', values: { tele: c.teleClosed, sale: c.closed },
      how: { tele: 'coi đã đến là đã chốt', sale: 'khách có đơn' },
      cause: 'Telesale chưa lấy theo đơn POS.',
    },
    {
      key: 'revenue', label: 'Doanh thu', money: true, values: { tele: c.teleRevenue || undefined, sale: c.revenue },
      how: { tele: 'giá trị dịch vụ đã đặt', sale: 'tiền đơn đã thu' },
      cause: 'Telesale là giá trị đặt lịch, chưa phải tiền thu.',
    },
  ];
  return rows.filter(r => Object.values(r.values).filter(v => v !== undefined).length >= 2);
}

function ReconcileCard({ r }: { r: Row }) {
  const items = ORDER.filter(k => r.values[k] !== undefined).map(k => ({ k, v: r.values[k]! }));
  const vals = items.map(x => x.v);
  const max = Math.max(1, ...vals);
  const off = isMismatch(vals);
  const gap = Math.max(...vals) - Math.min(...vals);
  const show = (v: number) => (r.money ? fmtMoney(v) : Math.round(v).toLocaleString('vi-VN'));

  return (
    <div className={`flex flex-col rounded-xl border p-3 ${off ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span className="text-[13px] font-semibold text-slate-800">{r.label}</span>
        {off
          ? <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">Lệch {show(gap)}</span>
          : <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">Khớp</span>}
      </div>
      <ul className="space-y-2">
        {items.map(({ k, v }) => (
          <li key={k}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: SRC[k].color }} />
                <span className="shrink-0 text-slate-700">{SRC[k].label}</span>
                {r.how[k] && <span className="truncate text-[11px] text-slate-400" title={r.how[k]}>· {r.how[k]}</span>}
              </span>
              <b className="shrink-0 text-[13px] text-slate-800">{show(v)}</b>
            </div>
            <span className="mt-1 block h-1.5 overflow-hidden rounded-full bg-slate-100">
              <span className="block h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: SRC[k].color }} />
            </span>
          </li>
        ))}
      </ul>
      {off && <div className="mt-auto pt-2.5 text-[11px] leading-snug text-amber-700">{r.cause}</div>}
    </div>
  );
}

export function ReconcilePanel({ cur, c }: { cur: Snapshot; c: Totals }) {
  const rows = reconcileRows(cur, c);
  if (!rows.length) return null;
  const bad = rows.filter(r => isMismatch(Object.values(r.values).filter((v): v is number => v !== undefined))).length;

  return (
    <Panel
      className="mb-6" title="Đối soát số liệu giữa các bộ phận"
      info="Cùng một chỉ số nhưng mỗi bộ phận đếm theo cách riêng. Chênh hơn 10% được đánh dấu Lệch kèm nguyên nhân."
      extra={bad
        ? <span className="flex items-center gap-1 text-xs font-medium text-amber-600"><AlertTriangle size={13} />{bad}/{rows.length} chỉ số lệch</span>
        : <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><CheckCircle2 size={13} />Các bộ phận khớp nhau</span>}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map(r => <ReconcileCard key={r.key} r={r} />)}
      </div>
    </Panel>
  );
}
